import { useCallback, useEffect, useRef, useState } from 'react';

interface AudioState {
  isMuted: boolean;
  sfxVolume: number;
}

export const useSnakeAudio = () => {
  const [audioState, setAudioState] = useState<AudioState>({
    isMuted: false,
    sfxVolume: 0.5,
  });

  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && !audioContextRef.current) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        audioContextRef.current = new AudioContextClass();
      }
    }
  }, []);

  const playTone = useCallback((frequency: number, duration: number, volume: number = 0.5) => {
    if (!audioContextRef.current || audioState.isMuted) return;

    const context = audioContextRef.current;
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    oscillator.frequency.value = frequency;
    oscillator.type = 'square';
    gainNode.gain.setValueAtTime(volume * audioState.sfxVolume, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + duration);
    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + duration);
  }, [audioState.isMuted, audioState.sfxVolume]);

  const playEatSound = useCallback(() => {
    playTone(800, 0.1);
  }, [playTone]);

  const playCorrectSound = useCallback(() => {
    if (!audioContextRef.current || audioState.isMuted) return;
    
    playTone(523, 0.15); // C
    setTimeout(() => playTone(659, 0.15), 150); // E
    setTimeout(() => playTone(784, 0.2), 300); // G
  }, [playTone, audioState.isMuted]);

  const playWrongSound = useCallback(() => {
    if (!audioContextRef.current || audioState.isMuted) return;
    
    playTone(200, 0.2, 0.4);
    setTimeout(() => playTone(150, 0.3, 0.4), 200);
  }, [playTone, audioState.isMuted]);

  const playGameOverSound = useCallback(() => {
    if (!audioContextRef.current || audioState.isMuted) return;
    
    playTone(400, 0.2);
    setTimeout(() => playTone(350, 0.2), 200);
    setTimeout(() => playTone(300, 0.2), 400);
    setTimeout(() => playTone(250, 0.4), 600);
  }, [playTone, audioState.isMuted]);

  const toggleMute = useCallback(() => {
    setAudioState(prev => ({ ...prev, isMuted: !prev.isMuted }));
  }, []);

  const setSfxVolume = useCallback((volume: number) => {
    setAudioState(prev => ({ ...prev, sfxVolume: volume }));
  }, []);

  return {
    playEatSound,
    playCorrectSound,
    playWrongSound,
    playGameOverSound,
    toggleMute,
    isMuted: audioState.isMuted,
    sfxVolume: audioState.sfxVolume,
    setSfxVolume,
  };
};

